import { isEmpty } from 'lodash';
import { observer } from 'mobx-react';
import React from 'react';
import { Link } from 'react-router-dom';

import { StraightOutlined } from '@mui/icons-material';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
    Avatar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Tooltip
} from '@mui/material';

import Label from 'pages/Search/components/Database/components/Label';
import Score from 'pages/Search/components/Database/components/Score';
import RowMenu from 'pages/Search/components/Database/components/SearchResultsTable/components/RowMenu';
import { DATA_COLUMNS } from 'pages/Search/components/Database/components/SearchResultsTable/constants';
import Tag from 'pages/Search/components/Database/components/Tag';
import useFormatDate from 'pages/Search/components/Database/hooks/use-format-date';
import { FormattedResponseItem } from 'pages/Search/components/Database/types';

import useSortingState from './hooks/use-sorting-state';
import useStyles from './styles';

interface Props {
    data: FormattedResponseItem[];
    className?: string;
}

const SearchResultsTable: React.FC<Props> = ({ data, className }) => {
    const { classes, cx } = useStyles();
    const formatDate = useFormatDate({ time: false });
    const sorting = useSortingState();

    return (
        <TableContainer className={cx(classes.container, className)}>
            <Table className={classes.table}>
                <TableHead>
                    <TableRow className={classes.header}>
                        <TableCell padding="checkbox" />
                        {DATA_COLUMNS.map(({ field, label }) => {
                            return (
                                <TableCell key={field}>
                                    <TableSortLabel
                                        disabled={sorting.disabled}
                                        IconComponent={StraightOutlined}
                                        active={sorting.isActive(field)}
                                        direction={sorting.isActive(field) ? sorting.direction : 'desc'}
                                        onClick={() => sorting.toggle(field)}
                                    >
                                        {label}
                                    </TableSortLabel>
                                </TableCell>
                            );
                        })}
                        <TableCell padding="checkbox" />
                    </TableRow>
                </TableHead>
                <TableBody className={classes.body}>
                    {data.map((row, index) => (
                        <TableRow key={index} className={classes.row}>
                            <TableCell padding="checkbox">
                                <Link to={row.href} className={classes.link}>
                                    <Avatar className={classes.avatar} src={row.image} alt={row.type} />
                                </Link>
                            </TableCell>
                            <TableCell scope="row">
                                {
                                    <Tooltip
                                        title={
                                            isEmpty(row.tags)
                                                ? null
                                                : row.tags.map((label, index) => <Tag key={index} label={label} />)
                                        }
                                        classes={{ tooltip: classes.tooltip }}
                                    >
                                        <Link to={row.href} className={classes.link}>
                                            <div
                                                className={cx(classes.vulnerability, {
                                                    [classes._interactive]: row.tags.length > 0
                                                })}
                                            >
                                                <span className={classes.title}>{row.title}</span>
                                                <span className={classes.description}>{row.description}</span>
                                            </div>
                                        </Link>
                                    </Tooltip>
                                }
                            </TableCell>
                            <TableCell>{formatDate(row.published)}</TableCell>
                            <TableCell>{row.cvssScore ? <Score value={row.cvssScore} /> : null}</TableCell>
                            <TableCell>{row.aiScore ? <Score value={row.aiScore} /> : null}</TableCell>
                            <TableCell>{row.epssScore ? <Score value={row.epssScore} /> : null}</TableCell>
                            <TableCell>
                                {row.isWild ? (
                                    <Label
                                        icon={<FiberManualRecordIcon />}
                                        compact
                                        variant="danger"
                                        text="In wild"
                                        className={classes.inWild}
                                    />
                                ) : null}
                            </TableCell>
                            <TableCell>
                                {row.webApplicable ? (
                                    <Label icon={<LanguageOutlinedIcon />} compact text="Web" className={classes.web} />
                                ) : null}
                            </TableCell>
                            <TableCell>
                                {
                                    <span className={classes.seen}>
                                        <VisibilityOutlinedIcon />
                                        {row.views}
                                    </span>
                                }
                            </TableCell>
                            <TableCell>
                                <div className={classes.actions}>
                                    <RowMenu data={row} />
                                    <Link to={row.href} className={classes.link}>
                                        <ChevronRightOutlinedIcon />
                                    </Link>
                                </div>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default observer(SearchResultsTable);
